import { useQuery } from '@tanstack/react-query'
import { getPipelineProgress } from '@/services/api'
import { cn } from '@/lib/utils'

interface PipelineStage {
  name: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped' | 'unknown'
  order: number
  started_at: string | null
  finished_at: string | null
}

interface PipelineProgressProps {
  requestId: string
  overallStatus: string
}

export function PipelineProgress({ requestId, overallStatus }: PipelineProgressProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['pipeline-progress', requestId],
    queryFn: () => getPipelineProgress(requestId),
    enabled: !!requestId && (overallStatus === 'running' || overallStatus === 'approved' || overallStatus === 'completed' || overallStatus === 'failed'),
    refetchInterval: (query) => {
      // Poll every 5s if pipeline is running
      const status = query.state.data?.status
      return status === 'running' ? 5000 : false
    },
  })

  // Don't show anything if status is pending (not approved yet)
  if (overallStatus === 'pending') {
    return null
  }

  if (isLoading) {
    return (
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-bronze mb-4">Progression du Provisionnement</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold" />
        </div>
      </div>
    )
  }

  // Use new structure if available, fall back to old structure
  const infrastructure: PipelineStage[] = data?.infrastructure || []
  const deployment: PipelineStage[] = data?.deployment || []

  // Fallback for old API response
  const hasNewStructure = infrastructure.length > 0 || deployment.length > 0
  const legacyStages: PipelineStage[] = !hasNewStructure ? (data?.stages || []) : []

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-bronze mb-6">Progression du Provisionnement</h3>

      {hasNewStructure ? (
        <div className="space-y-6">
          {/* Infrastructure Track */}
          <PipelineTrack
            title="Infrastructure"
            stages={infrastructure}
            icon={<ServerIcon />}
          />

          {/* Deployment Track */}
          <PipelineTrack
            title="Deploiement"
            stages={deployment}
            icon={<RocketIcon />}
          />
        </div>
      ) : (
        /* Legacy single track */
        <div className="hidden md:block">
          <div className="flex items-center justify-between gap-2">
            {legacyStages.map((stage, index) => (
              <div key={stage.name} className="flex items-center flex-1">
                <div className="flex-1">
                  <StageBlock stage={stage} />
                </div>
                {index < legacyStages.length - 1 && (
                  <div className="flex-shrink-0 mx-1">
                    <ConnectorArrow fromStatus={stage.status} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border-2 border-muted-foreground/30 bg-transparent" />
          <span>En attente</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border-2 border-gold bg-gold/20 animate-pulse" />
          <span>En cours</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border-2 border-green-500 bg-green-500" />
          <span>Terminé</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border-2 border-red-500 bg-red-500" />
          <span>Échoué</span>
        </div>
      </div>
    </div>
  )
}

function PipelineTrack({ title, stages, icon }: { title: string; stages: PipelineStage[]; icon: React.ReactNode }) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      {/* Track header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-bronze">{icon}</span>
        <span className="text-sm font-semibold text-bronze">{title}</span>
      </div>

      {/* Desktop: Horizontal pipeline */}
      <div className="hidden md:block">
        <div className="flex items-center gap-2">
          {stages.map((stage, index) => (
            <div key={stage.name} className="flex items-center flex-1">
              <div className="flex-1">
                <StageBlock stage={stage} />
              </div>
              {index < stages.length - 1 && (
                <div className="flex-shrink-0 mx-1">
                  <ConnectorArrow fromStatus={stage.status} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: Vertical pipeline */}
      <div className="md:hidden space-y-2">
        {stages.map((stage, index) => (
          <div key={stage.name}>
            <StageBlockMobile stage={stage} />
            {index < stages.length - 1 && (
              <div className="flex justify-center py-1">
                <div className={cn(
                  "w-0.5 h-3",
                  stage.status === 'success' ? 'bg-green-400' : 'bg-border'
                )} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function StageBlock({ stage }: { stage: PipelineStage }) {
  const statusStyles = {
    pending: 'border-muted-foreground/30 bg-white text-muted-foreground',
    running: 'border-gold bg-gold/10 text-gold animate-pulse',
    success: 'border-green-500 bg-green-50 text-green-700',
    failed: 'border-red-500 bg-red-50 text-red-700',
    skipped: 'border-muted-foreground/30 bg-white text-muted-foreground',
    unknown: 'border-muted-foreground/30 bg-white text-muted-foreground',
  }

  const iconStyles = {
    pending: null,
    running: <SpinnerIcon />,
    success: <CheckIcon />,
    failed: <XIcon />,
    skipped: <SkipIcon />,
    unknown: <QuestionIcon />,
  }

  return (
    <div
      className={cn(
        'relative rounded-lg border-2 px-3 py-2 text-center transition-all duration-300',
        statusStyles[stage.status]
      )}
    >
      <div className="flex items-center justify-center gap-1.5">
        {iconStyles[stage.status]}
        <span className="text-xs font-medium truncate">{stage.name}</span>
      </div>
    </div>
  )
}

function StageBlockMobile({ stage }: { stage: PipelineStage }) {
  const statusStyles = {
    pending: 'border-muted-foreground/30 bg-white text-muted-foreground',
    running: 'border-gold bg-gold/10 text-gold',
    success: 'border-green-500 bg-green-50 text-green-700',
    failed: 'border-red-500 bg-red-50 text-red-700',
    skipped: 'border-muted-foreground/30 bg-white text-muted-foreground',
    unknown: 'border-muted-foreground/30 bg-white text-muted-foreground',
  }

  const iconStyles = {
    pending: null,
    running: <SpinnerIcon />,
    success: <CheckIcon />,
    failed: <XIcon />,
    skipped: <SkipIcon />,
    unknown: <QuestionIcon />,
  }

  return (
    <div
      className={cn(
        'relative rounded-lg border-2 px-4 py-2 transition-all duration-300',
        statusStyles[stage.status],
        stage.status === 'running' && 'animate-pulse'
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          {iconStyles[stage.status] || (
            <span className="w-4 h-4 rounded-full border-2 border-current opacity-50 block" />
          )}
        </div>
        <span className="text-sm font-medium">{stage.name}</span>
      </div>
    </div>
  )
}

function ConnectorArrow({ fromStatus }: { fromStatus: string }) {
  const isActive = fromStatus === 'success'

  return (
    <svg
      width="20"
      height="12"
      viewBox="0 0 20 12"
      className={cn(
        'transition-colors duration-300',
        isActive ? 'text-green-400' : 'text-muted-foreground/30'
      )}
    >
      <path
        d="M0 6h16M12 1l5 5-5 5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// Track Icons
function ServerIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="6" rx="1" />
      <rect x="2" y="15" width="20" height="6" rx="1" />
      <circle cx="6" cy="6" r="1" fill="currentColor" />
      <circle cx="6" cy="18" r="1" fill="currentColor" />
    </svg>
  )
}

function RocketIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  )
}

// Status Icons
function SpinnerIcon() {
  return (
    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function SkipIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 4h4v16H5zM15 4h4v16h-4z" />
    </svg>
  )
}

function QuestionIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}
